import * as Yup from 'yup';
import type { FC } from 'react';
import { Formik, FormikErrors, FormikTouched } from 'formik';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  FormHelperText,
  TextField,
  Checkbox,
  Typography,
  Link,
  FormControlLabel,
  CircularProgress,
  Stack
} from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import useRefMounted from 'src/hooks/useRefMounted';
import { useTranslation } from 'react-i18next';
import { isAdmin } from 'src/utils/auth';

const LoginJWT: FC = () => {
  const { login, user } = useAuth() as any;
  const isMountedRef = useRefMounted();
  const { t }: { t: any } = useTranslation();
  const nav = useNavigate();

  const scheme = Yup.object({
    email: Yup.string()
      .email(t('The email provided should be a valid email address'))
      .max(255)
      .required(t('The email field is required')),
    password: Yup.string()
      .max(255)
      .required(t('The password field is required')),
    terms: Yup.boolean().oneOf(
      [true],
      t('You must agree to our terms and conditions')
    )
  });

  return (
    <Formik
      initialValues={{
        email: 'demo@demo.com',
        password: 'demo@demo.com',
        terms: true,
        submit: null
      }}
      validationSchema={scheme}
      onSubmit={async (
        values,
        { setErrors, setStatus, setSubmitting }
      ): Promise<void> => {
        try {
          await login(values.email, values.password);

          if (isMountedRef()) {
            setStatus({ success: true });
            setSubmitting(false);
            nav("/")
          }
        } catch (err) {
          if (isMountedRef()) {
            setStatus({ success: false });
            setErrors({ submit: err.response.data.length > 0 ? err.response.data : err.message });
            setSubmitting(false);
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values
      }): JSX.Element => (
        <form noValidate onSubmit={handleSubmit}>
          <TextField
            error={Boolean(touched.email && errors.email)}
            fullWidth
            margin="normal"
            autoFocus
            helperText={touched.email && errors.email}
            label={t('Email address')}
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            type="email"
            value={values.email}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.password && errors.password)}
            fullWidth
            margin="normal"
            helperText={touched.password && errors.password}
            label={t('Password')}
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            type="password"
            value={values.password}
            variant="outlined"
            autoComplete='on'
          />
          <Box
            alignItems="center"
            display={{ xs: 'block', md: 'flex' }}
            justifyContent="space-between"
          >
            <Box display={{ xs: 'block', md: 'flex' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.terms}
                    name="terms"
                    color="primary"
                    onChange={handleChange}
                  />
                }
                label={
                  <>
                    <Typography variant="body2">
                      {t('I accept the')}{' '}
                      <Link component="a" href="#">
                        {t('terms and conditions')}
                      </Link>
                      .
                    </Typography>
                  </>
                }
              />
            </Box>
            {/* Todo in a later version */}
            {/* <Link component={RouterLink} to="/account/recover-password">
              <b>{t('Lost password?')}</b>
            </Link> */}

            {
              isAdmin(user?.role) ? (
                <Link component={RouterLink} to="/account/register">
                  <b>{t('Register new account?')}</b>
                </Link>
              ) : ''
            }
          </Box>

          {Boolean(touched.terms && errors.terms) && (
            <FormHelperText error>{errors.terms}</FormHelperText>
          )}
          <Stack spacing={2}>
            <Button
              sx={{
                mt: 3
              }}
              color="primary"
              startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
              disabled={isSubmitting}
              type="submit"
              fullWidth
              size="large"
              variant="contained"
            >
              {t('Sign in')}
            </Button>
            {
              user ? (
                <Link href="/">Go to dashboard{'>>'}</Link>
              ) : ''
            }
            {
              Boolean(touched.submit && errors.submit) && (
                <FormHelperText error>{errors.submit}</FormHelperText>
              )
            }
          </Stack>

        </form>
      )}
    </Formik>
  );
};
export default LoginJWT;